import { policyName, POLICY_IDS } from '@/features/decisions/config/personaContext';
import type { ReplenishmentRow } from '@/features/decisions/decisions.api.types';

// Backtest scenario colors: Incumbent muted; Keystone Quantity Only lighter green;
// Keystone Fully Optimized darkest green. Mapped by experiment_id (not column/series
// order) so the shades can't flip. Used by every backtest chart + sparkline.
export function backtestSeriesColor(experimentId: string | null | undefined, isReference = false): string {
  if (isReference) return '#94a3b8'; // Incumbent — muted slate
  if (experimentId === POLICY_IDS.keystoneOptimized) return '#047857'; // Fully Optimized — darkest green
  if (experimentId === POLICY_IDS.keystoneForecast) return '#34d399'; // Quantity Only — lighter green
  return '#065f46'; // any other comparison — fallback green
}

// Format a metric value by its declared unit (from HistoricalPerformanceMetric.unit).
export function fmtMetricValue(value: number | null | undefined, unit: string): string {
  if (value == null) return '—';
  if (unit === 'ratio') return `${(value * 100).toFixed(1)}%`;
  if (unit === 'usd') {
    const sign = value < 0 ? '-' : '';
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
    return `${sign}$${abs.toFixed(0)}`;
  }
  // units (and any unknown unit) — integer with thousands separators
  return Math.round(value).toLocaleString();
}

// Unit quantity, no inline suffix — the surrounding label/caption carries the
// unit ("Sale UOM"). Issue #125 dropped the terse "u".
export function fmtUnits(value: number | null | undefined): string {
  if (value == null) return '—';
  return Math.round(value).toLocaleString();
}

// Per-unit currency (Price / Cost in Policy Details): full dollars-and-cents.
export function fmtCurrencyPerUnit(value: number | null | undefined): string {
  if (value == null) return '—';
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

export function fmtServiceLevel(value: number | null | undefined): string {
  if (value == null) return '—';
  // model_target_service_level is a ratio (0.90 -> 90%)
  return `${(value * 100).toFixed(0)}%`;
}

// Friendly policy label. Resolves the product-facing name from the central
// POLICY_NAMES mapping; unmapped comparisons fall back to their experiment id.
export function policyLabel(row: Pick<ReplenishmentRow, 'experiment_id' | 'is_reference'>): string {
  return policyName(row.experiment_id, row.is_reference);
}
